// Camada de entitlements do LucraLink: fonte unica de verdade do gating por plano.
//
// Funcao pura, sem I/O e deterministica. E consumida pelo painel (esconder/travar
// UI PRO) e pelo bot (limitar grupos monitorados no Free). As Frentes B, C, D e
// outras dependem desta interface para fazer o gating de recursos pagos.
//
// Invariante de projeto: entitlement e permissao, NUNCA conversor. Travar um recurso
// por plano significa esconder UI ou recusar automacao, jamais substituir o gerador
// oficial de afiliado por um atalho que nao credita comissao. Qualquer recurso pago
// liberado continua usando o gerador oficial do marketplace.
export const FREE_MAX_GROUPS = 1;
/** Origens monitoradas do Free (espelhado em plan_limit, migration 20261003700001). */
export const FREE_MAX_MONITORED_SOURCES = 1;
export const MAX_CONNECTED_ACCOUNTS_PER_CHANNEL = 1;
/** Destinos do encaminhamento: Free 1, PRO 10 (espelhado em plan_limit,
 *  migration 20261003700001, usado pelo trigger enforce_forwarding_destination_limit). */
export const FREE_MAX_FORWARDING_DESTINATIONS = 1;
export const PRO_MAX_FORWARDING_DESTINATIONS = 10;
/** Promocoes automaticas do Free por dia (espelhado em plan_limit). PRO e ELITE: ilimitado. */
export const FREE_AUTO_PROMOTIONS_PER_DAY = 10;
/** Imagens IA: Free 0, PRO 5 vitalicias, ELITE 50 por mes (+ as 5 vitalicias).
 *  O limite efetivo vive no SQL (claim_ai_image_usage, redefinida em
 *  20261003700001 para ler o plano do banco). Mudar aqui exige mudar la. */
export const FREE_LIFETIME_AI_IMAGES = 0;
export const PRO_FREE_AI_IMAGES = 5;
export const PRO_AI_IMAGES_PER_MONTH = 50;
const FREE_ENTITLEMENTS = Object.freeze({
    plan: "free",
    tier: "free",
    maxLinks: null,
    maxGroups: FREE_MAX_GROUPS,
    maxMonitoredSources: FREE_MAX_MONITORED_SOURCES,
    maxConnectedAccountsPerChannel: MAX_CONNECTED_ACCOUNTS_PER_CHANNEL,
    hasShowcase: false,
    hasBioLink: false,
    hasFullAnalytics: false,
    hasWhatsappAutomation: true,
    hasShortener: false,
    hasExtension: false,
    hasCustomTemplates: false,
    hasTelegramBot: true,
    aiCopyRequiresOwnKey: true,
    aiImagesPerMonth: 0,
    freeAiImages: FREE_LIFETIME_AI_IMAGES,
    hasScheduledPosts: false,
    hasBulkSend: false,
    hasWatermark: false,
    hasGroupRotator: false,
    maxForwardingDestinations: FREE_MAX_FORWARDING_DESTINATIONS,
    autoPromotionsPerDay: FREE_AUTO_PROMOTIONS_PER_DAY,
    isPaid: false,
});
const PRO_ENTITLEMENTS = Object.freeze({
    ...FREE_ENTITLEMENTS,
    plan: "pro",
    tier: "pro",
    maxGroups: null,
    maxMonitoredSources: null,
    hasShowcase: true,
    hasBioLink: true,
    hasFullAnalytics: true,
    hasShortener: true,
    hasExtension: true,
    hasCustomTemplates: true,
    aiCopyRequiresOwnKey: false,
    freeAiImages: PRO_FREE_AI_IMAGES,
    hasScheduledPosts: true,
    hasBulkSend: true,
    hasWatermark: true,
    hasGroupRotator: true,
    maxForwardingDestinations: PRO_MAX_FORWARDING_DESTINATIONS,
    autoPromotionsPerDay: null,
    isPaid: true,
});
/** Tabela de capacidade por tier. Imutavel, congelada para evitar mutacao acidental. */
const ENTITLEMENTS_BY_PLAN = Object.freeze({
    free: FREE_ENTITLEMENTS,
    pro: PRO_ENTITLEMENTS,
    pro_ai: Object.freeze({
        ...PRO_ENTITLEMENTS,
        plan: "pro_ai",
        tier: "elite",
        aiImagesPerMonth: PRO_AI_IMAGES_PER_MONTH,
    }),
});
/** Planos pagos reconhecidos. Usado para validar entradas vindas do banco/JWT. */
const PAID_PLANS = new Set(["pro", "pro_ai"]);
/** Normaliza um plano cru (string vinda do banco/view) para um Plan conhecido.
 *  Qualquer valor desconhecido cai para "free" de forma segura, nunca lanca:
 *  defesa em profundidade contra dado corrompido degradando para o tier mais
 *  restritivo em vez de liberar capacidade indevida. */
export function normalizePlan(plan) {
    if (plan === "pro" || plan === "pro_ai" || plan === "free") {
        return plan;
    }
    return "free";
}
/** Indica se o plano efetivo expirou comparando proUntil com o instante atual.
 *  proUntil ausente/invalido nao expira por si so (o plano e quem decide). */
function isProExpired(proUntil, now) {
    if (proUntil === null || proUntil === undefined || proUntil === "") {
        return false;
    }
    const expiresAt = Date.parse(proUntil);
    if (Number.isNaN(expiresAt)) {
        // Data ilegivel: nao confiar nela como prova de validade. Trata como expirada
        // para nao manter PRO com base em lixo, deixando o plano cru decidir.
        return true;
    }
    return expiresAt <= now.getTime();
}
/**
 * Resolve as capacidades de um tenant a partir do plano e da validade PRO.
 *
 * Funcao pura: sem I/O, deterministica, testavel em painel e bot.
 *
 * Regra de expiracao (rede de seguranca): se o plano for pago e proUntil ja
 * passou, os entitlements caem para os de "free". O webhook do Stripe continua
 * sendo a fonte primaria de mudanca de plano; proUntil so cobre o caso de o
 * downgrade do webhook atrasar.
 *
 * @param plan plano do tenant (Plan ou string crua do banco, normalizada aqui).
 * @param proUntil validade PRO em ISO timestamptz. null/undefined = sem validade.
 * @param now instante de referencia, injetavel para testes deterministicos.
 */
export function resolveEntitlements(plan, proUntil, now = new Date()) {
    const normalized = normalizePlan(plan);
    if (PAID_PLANS.has(normalized) && isProExpired(proUntil, now)) {
        return ENTITLEMENTS_BY_PLAN.free;
    }
    return ENTITLEMENTS_BY_PLAN[normalized];
}
/** Nome comercial de cada tier, para badges, upsell e pagina de planos. */
export const PLAN_TIER_LABEL = Object.freeze({
    free: "Free",
    pro: "PRO",
    elite: "ELITE",
});
/** Rotulo comercial a partir do plano cru do banco (pro_ai = ELITE). */
export function planTierLabel(plan) {
    return PLAN_TIER_LABEL[ENTITLEMENTS_BY_PLAN[normalizePlan(plan)].tier];
}
const FEATURE_CHECK = Object.freeze({
    fullAnalytics: (ent) => ent.hasFullAnalytics,
    showcase: (ent) => ent.hasShowcase,
    bioLink: (ent) => ent.hasBioLink,
    shortener: (ent) => ent.hasShortener,
    extension: (ent) => ent.hasExtension,
    customTemplates: (ent) => ent.hasCustomTemplates,
    bulkSend: (ent) => ent.hasBulkSend,
    watermark: (ent) => ent.hasWatermark,
    groupRotator: (ent) => ent.hasGroupRotator,
    aiImagesMonthly: (ent) => ent.aiImagesPerMonth > 0,
});
/** Indica se as capacidades liberam o recurso. */
export function isFeatureAllowed(ent, feature) {
    return FEATURE_CHECK[feature](ent);
}
/** Menor plano que libera o recurso (para o CTA de upgrade apontar o tier certo). */
export function requiredPlanFor(feature) {
    const order = ["free", "pro", "pro_ai"];
    const found = order.find((plan) => FEATURE_CHECK[feature](ENTITLEMENTS_BY_PLAN[plan]));
    // Todo recurso mapeado e liberado por algum tier; pro_ai e o teto.
    return found ?? "pro_ai";
}
/** Indica se ainda cabe mais um item dado o limite (null = ilimitado). */
export function hasRemainingCapacity(limit, used) {
    return limit === null || used < limit;
}
//# sourceMappingURL=entitlements.js.map